import { computed, inject, Injector } from '@angular/core';
import {
  patchState,
  signalStore,
  type,
  withComputed,
  withMethods,
} from '@ngrx/signals';
import {
  removeAllEntities,
  removeEntity,
  setAllEntities,
  setEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { withDevtools } from '@ngrx-toolkit/core';
import { HttpErrorResponse } from '@angular/common/http';
import { ApiService } from 'src/api';
import { Recording } from 'src/api/models';
import {
  HTTP_STATUS_CODE_RECORDER_NO_SPACE,
  HTTP_STATUS_CODE_REMOVE_RECORDING_WITH_COMPETITION,
} from 'src/app/app.constants';
import { groupRecordingsByKey } from 'src/app/features/recordings/recording-list.utils';
import { dateKey } from 'src/app/shared/utils/date.utils';
import { GoogleAnalyticsEvent } from 'src/app/shared/enums/google-analytics-event.enum';
import { RecordingStore } from './recording.store';
import { RecordingListStore } from './recording-list.store';
import { withApiOperation } from './features/with-api-operation.feature';
import { withRequestStatus } from './features/with-request-status.feature';

export const TrashStore = signalStore(
  withDevtools('trash'),
  withRequestStatus(),
  withApiOperation(),
  withEntities({ entity: type<Recording>() }),
  withComputed(store => ({
    recordingsInTrash: computed(() => {
      const recordingsByRecorder = groupRecordingsByKey(
        store.entities(),
        (recording: Recording) => recording.recorderId,
      );
      const recordingGroup: Partial<
        Record<string, { [key: string]: Recording[] }>
      > = {};
      Object.keys(recordingsByRecorder).forEach(
        recorderId =>
          (recordingGroup[recorderId] = groupRecordingsByKey(
            recordingsByRecorder[recorderId],
            (recording: Recording) => dateKey(recording.dateTime),
          )),
      );

      return recordingGroup;
    }),
  })),
  withMethods(store => {
    const apiClient = inject(ApiService);
    // Lazy to avoid a construction cycle with the recording stores.
    const injector = inject(Injector);

    return {
      async refresh() {
        await store.runApiOperation({
          request: apiClient.trashGet(),
          onSuccess: recordings => {
            patchState(store, setAllEntities(recordings));
            store.setLoaded();
          },
          onError: error => store.handleApiError(error),
        });
      },
      async remove(id: string) {
        await store.runApiOperation({
          request: apiClient.trashIdPost({ id }),
          event: GoogleAnalyticsEvent.RECORDING_MOVE_TO_TRASH,
          successSnackbar: {
            message: 'success.trash.remove',
            icon: 'heroTrash',
          },
          errorSnackbar: error => ({
            message: `error.trash.remove.${
              (error as HttpErrorResponse).status ===
              HTTP_STATUS_CODE_REMOVE_RECORDING_WITH_COMPETITION
                ? HTTP_STATUS_CODE_REMOVE_RECORDING_WITH_COMPETITION.toString()
                : 'default'
            }`,
            icon: 'heroXMark',
          }),
          onSuccess: recording => {
            injector.get(RecordingStore).notifyRecordingDeleted(recording.id);
            patchState(store, setEntity(recording), {
              loading: false,
              errors: [],
            });
          },
        });
      },
      /* Removes a restored recording from the trash without calling the API
       * (used when another client restores it, signalled over WebSocket). */
      handleRestored(recording: Recording) {
        patchState(store, removeEntity(recording.id), {
          loading: false,
          errors: [],
        });
      },
      async restore(id: string) {
        await store.runApiOperation({
          request: apiClient.trashIdRestorePost({ id }),
          event: GoogleAnalyticsEvent.RECORDING_RESTORE,
          successSnackbar: {
            message: 'success.trash.restore',
            icon: 'heroCheck',
          },
          errorSnackbar: error => ({
            message:
              (error as HttpErrorResponse).status ===
              HTTP_STATUS_CODE_RECORDER_NO_SPACE
                ? 'error.trash.notEnoughtSpace'
                : 'error.trash.restore',
            icon: 'heroXMark',
          }),
          onSuccess: recording => {
            this.handleRestored(recording);
            injector.get(RecordingListStore).handleRestored(recording);
          },
        });
      },
      async empty() {
        await store.runApiOperation({
          request: apiClient.trashDelete(),
          successSnackbar: {
            message: 'success.trash.empty',
            icon: 'heroTrash',
          },
          errorSnackbar: {
            message: 'error.trash.empty',
            icon: 'heroXMark',
          },
          onSuccess: () =>
            patchState(store, removeAllEntities(), {
              loading: false,
              errors: [],
            }),
        });
      },
    };
  }),
);
